import { createContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { lightTokens, darkTokens } from './tokens';


// 1. Context 생성
export const ThemeContext = createContext();

// 2. ThemeProvider
export const ThemeProvider = ({ children }) => {
  
  // 2-0. Dark Mode 상태 (Mute Soyo는 라이트 모드로 고정!)
  // const [isDarkMode, setIsDarkMode] = useState(false);

  // 2-1. Dark Mode 상태
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('isDarkMode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // 2-2. Mode 및 token 선택
  const mode = isDarkMode ? 'dark' : 'light';
  const tokens = isDarkMode ? darkTokens : lightTokens;

  // 2-3. side effect: localStorage 및 body class
  useEffect(() => {
    localStorage.setItem('isDarkMode', isDarkMode);
    document.documentElement.setAttribute('data-bs-theme', mode);
    document.body.classList.toggle('dark-theme', isDarkMode);

    applyCssVariables(tokens); // CSS 변수 적용
  }, [isDarkMode, mode, tokens]);

  // 3. MUI Theme 생성
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode,
        contrastThreshold: 4.5,
        primary: { main: tokens.color.primary },
        secondary: { main: tokens.color.secondary },
        secondaryDark: { main: tokens.color.secondaryDark },
        highlight: { main: tokens.color.highlight },
        background: { default: tokens.color.background },
        text: { primary: tokens.color.text },
      },
      status: {
        danger: tokens.color.danger,
      },
    });
  }, [tokens, mode]);

  // 4. Context value
  const value = useMemo(() => ({ isDarkMode, setIsDarkMode }), [isDarkMode]);

  // 5. CSS 변수 적용 함수
  const toKebabCase = (str) =>
    str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

  const applyCssVariables = (tokens, prefix = 'hoza') => {
    const root = document.documentElement;

    // 5-1. color
    Object.entries(tokens.color).forEach(([key, value]) => {
      const kebabKey = toKebabCase(key);
      root.style.setProperty(`--${prefix}-${kebabKey}`, value);
    });

    // 5-2. spacing
    if (tokens.spacing) {
      Object.entries(tokens.spacing).forEach(([key, value]) => {
        const kebabKey = toKebabCase(key);
        root.style.setProperty(`--${prefix}-spacing-${kebabKey}`, value);
      });
    }

    // 5-3. radius
    if (tokens.radius) {
      Object.entries(tokens.radius).forEach(([key, value]) => {
        const kebabKey = toKebabCase(key);
        root.style.setProperty(`--${prefix}-radius-${kebabKey}`, value);
      });
    }

    // 5-4. Bootstrap 변수 자동 매핑
    Object.keys(tokens.color).forEach((key) => {
      const kebabKey = toKebabCase(key);
      root.style.setProperty(`--bs-${kebabKey}`, `var(--${prefix}-${kebabKey})`);
    });
    
  };

  // 6. Provider 반환
  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};